import { useContext, useMemo, useState } from "react";
import { Box, CircularProgress, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  InnerWrapper,
  MainContainer,
  TitleAndActionBox,
  FilterNonIdeal,
} from "../../../../common";
import AppTableFilters from "../../../../common/AppTableFilters";
import { AppPagination, UBButton } from "../../../../../Libraries";
import { DoubleArrowIcon } from "../../../../../assets/icons/icon.jsx";
import { analytics, triggerLogEvent } from "../../../../../config/firebase";
import { buildFiltersConfig } from "../../../../../utils";
import moment from "moment";
import { formatNum } from "../../../../../oscar-pos-core/constants";
import useTransactionListing, { initialListingFilters } from "../../../../../hooks/useTransactionListing";
import useVoucherCustomers from "../../../../../hooks/useVoucherCustomers";
import useVoucherVendors from "../../../../../hooks/useVoucherVendors";
import useEmptyListingRedirect from "../../../../../hooks/useEmptyListingRedirect";
import useSellVoucherNavigation from "../../../../../hooks/useSellVoucherNavigation";
import AddCustomerModal from "../../../../common/Modals/AddCustomerModal";
import ContactRow from "../../TransactionListing/ContactRow";
import VendorRow from "./VendorRow";
import VoucherTransactionRow from "./VoucherTransactionRow";
import walletMainStyles from "../../WalletMain.module.css";
import styles from "../../TransactionListing/TransactionListing.module.css";
import { LanguageContext } from "../../../../../context/LanguageContext";

const VOUCHER_TXN_TYPES = ["voucher_payment"];

const getViewOptions = (I18n) => [
  { id: "contact", label: I18n.contact, value: "contact" },
  { id: "transaction", label: I18n.transaction, value: "transaction" },
];

const VoucherTransactionListingPage = ({
  customerPhone = null,
  customerName = null,
  recentTransactions = null,
  voucherContactsCacheRef = null,
}) => {
  const navigate = useNavigate();
  const [I18n] = useContext(LanguageContext);
  const { goToSellVouchers, isSellVoucherLoading } = useSellVoucherNavigation();

  const [addContactModal, setAddContactModal] = useState(false);
  const [addContactInitName, setAddContactInitName] = useState("");
  const [contactSearchQ, setContactSearchQ] = useState("");
  const [isResetFilter, setIsResetFilter] = useState(false);

  const isContactDetail = Boolean(customerPhone);

  const {
    searchQ,
    setSearchQ,
    filters,
    setFilters,
    transactions,
    hasNext,
    isLoading,
    isInitialLoading,
    isError,
    companyOptions,
    handleLoadMore,
    handleFilterChange,
    handleRemoveChip,
    resetAndFetch,
  } = useTransactionListing({ txnTypes: VOUCHER_TXN_TYPES, customerPhone });

  const isTransactionView = filters.view === "transaction";

  // L1 contact view fetches from the dedicated voucher customers API
  const voucherCustomers = useVoucherCustomers({
    search: contactSearchQ,
    enabled: !isContactDetail && !isTransactionView,
    cacheRef: !isContactDetail ? voucherContactsCacheRef : null,
  });

  // L2 contact detail view fetches the vendor list for a specific customer
  const voucherVendors = useVoucherVendors({
    customerPhone: isContactDetail ? customerPhone : null,
    seedData: isContactDetail ? (recentTransactions || null) : null,
  });

  const filtersConfig = useMemo(() => {
    const viewConfig = {
      id: "view",
      label: I18n.view,
      type: "sort",
      options: getViewOptions(I18n),
    };

    const [searchConfig] = buildFiltersConfig(["keywords"], [], null, null, I18n.search_by_name_or_phone, null, null, null, null, I18n);

    if (!isTransactionView) {
      // L2 vendor list has no search of its own — only the View toggle
      return isContactDetail ? [viewConfig] : [viewConfig, searchConfig];
    }

    const base = buildFiltersConfig(["date", "amount"], [], null, null, I18n.search_by_name_or_phone, null, null, null, null, I18n);
    const result = [
      viewConfig,
      ...base,
      { id: "companies", label: I18n.company, type: "checkbox", options: companyOptions, disabled: !companyOptions.length, preserveCase: true },
      searchConfig,
    ];
    return result;
  }, [isTransactionView, isContactDetail, companyOptions]);

  const activeSearchQ = isTransactionView ? searchQ : contactSearchQ;

  const filterState = useMemo(() => ({
    view: filters.view,
    keywords: activeSearchQ,
    date: { startDate: filters.startDate ?? null, endDate: filters.endDate ?? null },
    amount: {
      min: filters.fromAmount != null && filters.fromAmount !== "" ? String(filters.fromAmount) : "",
      max: filters.toAmount != null && filters.toAmount !== "" ? String(filters.toAmount) : "",
    },
    companies: filters.companies || [],
  }), [filters, activeSearchQ]);

  const appliedChips = useMemo(() => {
    const chips = [];
    if (isTransactionView) {
      chips.push({ id: "view", label: I18n.view, value: I18n.transaction });
    }
    if ((activeSearchQ || "").trim()) {
      chips.push({ id: "keywords", label: "Search", value: activeSearchQ.trim(), count: 1 });
    }
    if (isTransactionView) {
      if (filters.startDate || filters.endDate) {
        chips.push({
          id: "date",
          label: I18n.date,
          value: `${filters.startDate ? moment(filters.startDate).format("MMM DD") : (I18n.start || "Start")} - ${filters.endDate ? moment(filters.endDate).format("MMM DD") : (I18n.end || "End")}`,
        });
      }
      if (filters.fromAmount || filters.toAmount) {
        chips.push({
          id: "amount",
          label: I18n.amount,
          value: `Rs. ${formatNum(filters.fromAmount || 0)} - ${filters.toAmount ? formatNum(filters.toAmount) : "∞"}`,
        });
      }
      if (filters.companies?.length) {
        chips.push({
          id: "companies",
          label: I18n.company,
          value: filters.companies.length > 1 ? `(${filters.companies.length})` : filters.companies[0],
          count: filters.companies.length,
        });
      }
    }
    return chips;
  }, [filters, activeSearchQ, isTransactionView]);

  const isAppliedFilter = appliedChips.length > 0 || (activeSearchQ || "").trim().length > 0;

  const handleClearAllFilters = () => {
    setSearchQ("");
    setContactSearchQ("");
    setFilters(initialListingFilters);
    resetAndFetch(initialListingFilters, "");
    setIsResetFilter(true);
  };

  const handleContactViewDetail = (contact) => {
    triggerLogEvent(analytics, "w_voucher_txn_contact_detail");
    navigate("/UCash/VoucherTransactions", {
      state: {
        customerPhone: contact.customer_phone,
        customerName: contact.display_name,
        recentTransactions: contact.recent_transactions || null,
      },
    });
  };

  const handleAddContact = (item) => {
    setAddContactInitName(item.display_name || "");
    setAddContactModal(true);
  };

  const handleRowClick = (item) => {
    if (item.id) {
      navigate(`/UCash/TransactionDetail/voucher_payment/${item.id}`);
    }
  };

  const handleViewVendorTransactions = (vendor) => {
    navigate("/UCash/VoucherTransactions", {
      state: {
        customerPhone,
        customerName,
        company: vendor.company_name,
        vendor_id: vendor?.vendor_id,
        vendorTransactions: vendor.recent_transactions || null,
      },
    });
  };

  const isContactListView = !isTransactionView && !isContactDetail;
  const isContactDetailVendorView = isContactDetail && !isTransactionView;

  const currentIsInitialLoading = isContactListView
    ? voucherCustomers.isInitialLoading
    : isContactDetailVendorView
      ? voucherVendors.isInitialLoading
      : isInitialLoading;

  const currentIsLoading = isContactListView
    ? voucherCustomers.isLoading
    : isContactDetailVendorView
      ? voucherVendors.isLoading
      : isLoading;

  const currentHasNext = isContactListView
    ? voucherCustomers.hasNext
    : isContactDetailVendorView
      ? voucherVendors.hasNext
      : hasNext;

  const currentHandleLoadMore = isContactListView
    ? voucherCustomers.handleLoadMore
    : isContactDetailVendorView
      ? voucherVendors.handleLoadMore
      : handleLoadMore;

  const currentListEmpty = isContactListView
    ? voucherCustomers.contacts.length === 0
    : isContactDetailVendorView
      ? voucherVendors.vendors.length === 0
      : transactions.length === 0;

  // Landing on the top-level listing with genuinely no data (not filtered, not a
  // specific contact's history, and not because the fetch itself failed) — send the
  // user straight to sell flow instead of a dead end.
  const currentIsError = isContactListView ? voucherCustomers.isError : isError;
  useEmptyListingRedirect(
    !currentIsInitialLoading && currentListEmpty && !isAppliedFilter && !isContactDetail && !currentIsError,
    "/UCash/Vouchers",
  );

 const pageTitle = isContactDetail && customerName ? customerName : I18n.vouchers;
  const detailColumnLabel = isContactDetail ? I18n.details : I18n.contact;

  const sellVoucherBtn = (
    <UBButton
      id="voucher_transaction_sell_voucher_btn"
      title={I18n.sell_voucher}
      loading={isSellVoucherLoading}
      disabled={isSellVoucherLoading}
      className="ubButtonStyle ubPrimaryButton textUppercase fontSize14"
      onClick={() => goToSellVouchers(isContactDetail ? {
        state: { prefilledCustomerPhone: customerPhone, prefilledCustomerName: customerName },
      } : undefined)}
    />
  );

  return (
    <MainContainer fluid className="quickSalePage">
      <Grid container>
        <Grid item md={12}>
          <TitleAndActionBox
            data={{
              title: pageTitle,
              isBack: true,
              onBack: isContactDetail ? () => navigate(-1) : () => navigate("/UCash"),
              id: "voucher_transaction_listing",
              isTitleCase: false
            }}
          >
            <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {sellVoucherBtn}
            </Box>
          </TitleAndActionBox>

          <InnerWrapper className="innerWrapper newInnerWrapper">
            <AppTableFilters
              filtersConfig={filtersConfig}
              filterState={filterState}
              isResetFilter={isResetFilter}
              setIsResetFilter={setIsResetFilter}
              onFilterChange={(key, value) => {
                triggerLogEvent(analytics, "w_voucher_txn_filter_applied");
                if (String(key).toLowerCase() === "keywords" && !isTransactionView && !isContactDetail) {
                  setContactSearchQ(value ?? "");
                  return;
                }
                handleFilterChange(key, value);
              }}
              appliedChips={appliedChips}
              onRemoveChip={(chip) => {
                triggerLogEvent(analytics, "w_voucher_txn_filter_removed");
                if (chip.id === "keywords" && !isTransactionView && !isContactDetail) {
                  setContactSearchQ("");
                  return;
                }
                handleRemoveChip(chip);
              }}
            >
              {!isTransactionView && (
                <UBButton
                  id="voucher_txn_view_transactions_btn"
                  title={I18n.transactions}
                  iconComponent={<DoubleArrowIcon width={12} height={12} />}
                  className={styles.viewTransactionsBtn}
                  onClick={() => handleFilterChange("view", "transaction")}
                />
              )}
            </AppTableFilters>

            <Box key={`${isTransactionView ? "txn" : "contact"}-${isContactDetail ? "detail" : "list"}`}>
              {isTransactionView ? (
                <Box className="invoiceListTableHeader accountTableHeaderMain">
                  <Box className="invListTableHeaderCol fiveColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.date}</span>
                  </Box>
                  <Box className="invListTableHeaderCol fiveColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.company}</span>
                  </Box>
                  <Box className="invListTableHeaderCol fiveColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.type}</span>
                  </Box>
                  <Box className="invListTableHeaderCol fiveColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{detailColumnLabel}</span>
                  </Box>
                  <Box className="invListTableHeaderCol fiveColumn textRight">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.amount}</span>
                  </Box>
                </Box>
              ) : isContactDetail ? (
                <Box className="invoiceListTableHeader accountTableHeaderMain">
                  <Box className="invListTableHeaderCol fourColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.company}</span>
                  </Box>
                  <Box className="invListTableHeaderCol fourColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.phone_number}</span>
                  </Box>
                </Box>
              ) : (
                <Box className="invoiceListTableHeader accountTableHeaderMain">
                  <Box className="invListTableHeaderCol fourColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.contact}</span>
                  </Box>
                  <Box className="invListTableHeaderCol fourColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.phone_number}</span>
                  </Box>
                  <Box className="invListTableHeaderCol fourColumn textLeft">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.no_of_transactions}</span>
                  </Box>
                  <Box className="invListTableHeaderCol fourColumn justifyCenter">
                    <span className="fontSize12 fontWeight500 newLightColor">{I18n.actions}</span>
                  </Box>
                </Box>
              )}

              <Box className={`${styles.listWrapper} generic-scrollbar`} id="voucherTxnListScrollable">
                {currentIsInitialLoading ? (
                  <Box className={`${walletMainStyles.transactionListLoadingBox} h-100`}>
                    <CircularProgress size={25} />
                  </Box>
                ) : currentListEmpty ? (
                  <Box className="h-100">
                    {isAppliedFilter ? (
                      <FilterNonIdeal
                        title={I18n.no_transactions_matching_those_filters}
                        subTitle={I18n.try_adjusting_filters}
                        subTitleText={I18n.reset_small}
                        subTitleTextOnClick={handleClearAllFilters}
                      />
                    ) : (
                      <FilterNonIdeal
                        title={I18n.no_transactions_found}
                        subTitle={I18n.transactions_will_appear_here_once_available}
                      />
                    )}
                  </Box>
                ) : (
                  <AppPagination
                    key={isTransactionView ? "txn-pager" : "contact-pager"}
                    data={{
                      handleNext: currentHandleLoadMore,
                      pageLoad: currentIsLoading,
                      count: currentHasNext,
                      list: isContactListView
                        ? voucherCustomers.contacts
                        : isContactDetailVendorView
                          ? voucherVendors.vendors
                          : transactions,
                      scrollableTarget: "voucherTxnListScrollable",
                    }}
                  >
                    {isTransactionView
                      ? transactions.map((item, idx) => (
                        <VoucherTransactionRow key={item.id || idx} item={item} onRowClick={handleRowClick} />
                      ))
                      : isContactDetail
                        ? voucherVendors.vendors.map((item, idx) => (
                          <VendorRow
                            key={`${item.company_name}_${idx}`}
                            item={item}
                            onViewTransactions={handleViewVendorTransactions}
                          />
                        ))
                        : voucherCustomers.contacts.map((item, idx) => (
                          <ContactRow
                            key={item.customer_id || `${item.display_name}_${idx}`}
                            item={item}
                            onViewDetail={handleContactViewDetail}
                            onAddContact={handleAddContact}
                          />
                        ))}
                  </AppPagination>
                )}
              </Box>
            </Box>
          </InnerWrapper>
        </Grid>
      </Grid>

      <AddCustomerModal
        isOpen={addContactModal}
        handleClose={() => setAddContactModal(false)}
        initialName={addContactInitName}
        initialPhone=""
        type="in"
        title={I18n.add_contact}
        isTypeField={false}
        isOnlyPhoneList={true}
        onSaveCallback={() => setAddContactModal(false)}
      />
    </MainContainer>
  );
};

export default VoucherTransactionListingPage;
